import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, BRAND } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { HERO } from "@/constants/testIds";
import { ArrowRight, Zap, Package, Sparkles, Repeat, Timer, ShieldCheck, Play } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

export default function Home() {
  const { t, i18n } = useTranslation();
  const { format } = useCurrency();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [carouselApi, setCarouselApi] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/reviews").then((r) => setReviews(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  useEffect(() => {
    if (!carouselApi) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const root = carouselApi.rootNode();
    let paused = false;
    const enter = () => { paused = true; };
    const leave = () => { paused = false; };
    root.addEventListener("mouseenter", enter);
    root.addEventListener("focusin", enter);
    root.addEventListener("mouseleave", leave);
    root.addEventListener("focusout", leave);
    const id = setInterval(() => {
      if (!paused && document.visibilityState === "visible") carouselApi.scrollNext();
    }, 5500);
    return () => {
      clearInterval(id);
      root.removeEventListener("mouseenter", enter);
      root.removeEventListener("focusin", enter);
      root.removeEventListener("mouseleave", leave);
      root.removeEventListener("focusout", leave);
    };
  }, [carouselApi]);

  const kit = products.find((p) => p.slug === "preshhgo-starter-kit");
  const ACCESSORY_ORDER = ["preshh-1l-bottle", "preshh-2l-bottle", "preshh-caps-3pack", "portable-regulator", "preshh-dock-valve", "portable-cylinder-holder", "co2-adapter"];
  const accessories = ACCESSORY_ORDER.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div className="text-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0">
          <img src={BRAND.hero} alt="" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-32 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="uppercase text-xs tracking-[0.3em] text-[#00B5D8] mb-6">{t("hero.kicker")}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              {t("hero.title_line1")}<br/>{t("hero.title_line2")}<br/>
              <span className="text-[#00B5D8]">{t("hero.title_accent")}</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/preshhgo" data-testid={HERO.buyNow} className="preshh-btn-primary inline-flex items-center gap-2">
                {t("hero.cta_primary")} <ArrowRight size={18} />
              </Link>
              <Link to="/how-it-works" data-testid={HERO.learnMore} className="preshh-btn-ghost inline-flex items-center gap-2">
                <Play size={16} /> {t("hero.cta_secondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT DEMO VIDEO */}
      <section className="section-pad max-w-7xl mx-auto" data-testid="home-demo-video-section">
        <div className="max-w-2xl mb-8">
          <p className="preshh-eyebrow mb-3">{t("home_demo.eyebrow")}</p>
          <h2 className="preshh-section-title">{t("home_demo.title")}</h2>
          <p className="mt-4 preshh-section-lead text-white/60">{t("home_demo.lead")}</p>
        </div>
        <div className="relative aspect-video rounded-3xl overflow-hidden bg-[#111] border border-white/10 shadow-2xl">
          <video
            data-testid="home-demo-video"
            className="w-full h-full object-cover"
            src="/videos/preshh-demo-en.mp4"
            poster={BRAND.open}
            controls
            playsInline
            preload="metadata"
          />
        </div>
      </section>

      {/* WHAT IS */}
      <section className="section-pad max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5">
            <p className="preshh-eyebrow mb-4">What is Preshh GO</p>
            <h2 className="preshh-section-title">
              A versatile carbonation system for modern drink makers.
            </h2>
            <p className="mt-6 preshh-section-lead">
              Preshh GO is more than a carbonator — it's a compact tool for creating cocktails, sodas, spritzes and original drinks with fine, lively bubbles that add texture, freshness and character to whatever you pour.
            </p>
            <p className="mt-4 preshh-section-lead text-white/60">
              Built with durable materials, designed for repeated use, and remarkably efficient with CO₂ — so every drink feels crafted, and every canister goes a long way.
            </p>
          </div>
          <div className="md:col-span-7 relative aspect-[5/4] md:aspect-[4/3] rounded-3xl overflow-hidden bg-[#111]">
            <img src={BRAND.open} alt="Preshh GO open kit" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section-pad bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="preshh-section-title mb-4 max-w-2xl">Everything you need. Nothing you don't.</h2>
          <p className="preshh-section-lead max-w-2xl mb-16">
            A complete, thoughtfully designed system — compact enough to travel with you, capable enough to elevate your drinks. Everything included is there for a reason.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              [Package, "Compact & Portable", "Fits in a bag. Ready wherever the drink calls for it — home, event or behind the bar."],
              [Sparkles, "Endlessly Versatile", "Cocktails, sodas, spritzes, mocktails, juices, coffee, tea — one tool, countless recipes."],
              [Timer, "Ready in Minutes", "From still to sparkling in a few cycles. No waiting, no set-up ritual."],
              [Repeat, "Made to Last", "Durable components designed for daily, repeated use — not disposable."],
              [Zap, "Standard Bottles", "Use the bottles you already know. Preshh's specially designed caps let Preshh GO work with compatible standard PET bottles for extra flexibility."],
              [ShieldCheck, "Professional Feel", "Fine, lively bubbles that add texture, freshness and character — the same quality bartenders trust."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="bg-[#111] border border-white/10 rounded-2xl p-6 hover:border-[#00B5D8]/40 transition-colors">
                <Icon size={24} strokeWidth={1.5} className="text-[#00B5D8]" />
                <h3 className="preshh-card-title mt-4">{t}</h3>
                <p className="preshh-card-body mt-2">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xs text-white/40 max-w-xl">
            Looking for exact bottle compatibility, thread standards or component specs? See the <Link to="/faq" className="text-[#00B5D8] hover:underline">FAQ</Link> or the <a href={BRAND.manualUrl || "#"} className="text-[#00B5D8] hover:underline">User Manual</a>.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <p className="preshh-eyebrow mb-3">How it works</p>
            <h2 className="preshh-section-title max-w-xl">Three steps to sparkling perfection.</h2>
          </div>
          <Link to="/how-it-works" className="preshh-btn-ghost text-sm">Watch demo</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["01", "Fill the bottle", "Fill with your beverage and leave sufficient headspace for carbonation."],
            ["02", "Connect & inject CO₂", "Attach Preshh GO to the bottle and inject CO₂ from the portable regulator."],
            ["03", "Agitate & enjoy", "Follow the recommended agitation process and pour your carbonated drink."],
          ].map(([n, t, d]) => (
            <div key={n} className="relative bg-[#111] border border-white/10 rounded-3xl p-8 overflow-hidden">
              <div className="text-6xl font-display text-[#00B5D8]/30">{n}</div>
              <h3 className="preshh-card-title mt-4 text-2xl">{t}</h3>
              <p className="preshh-card-body mt-3">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT CAN I MAKE — mixed treatment: photograph for mapped niches, icon for pending ones */}
      <section className="section-pad bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <p className="preshh-eyebrow mb-3">What can you make?</p>
              <h2 className="preshh-section-title max-w-2xl">From sparkling water to signature cocktails.</h2>
            </div>
            <Link to="/arts" className="preshh-btn-arts" aria-label="Visit Preshh Arts">
              Preshh Arts <span className="preshh-btn-arts__arrow" aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Sparkles, title: "Sparkling Cocktails", body: "Vibrant bubbles that add texture and freshness to every serve.",
                image: "https://customer-assets-eiarnc6j.emergentagent.net/job_beverage-tech-1/artifacts/p2vyeupv_16052026-IMG_0160.webp" },
              { icon: Zap, title: "Sodas & Sparklers", body: "Homemade sodas with control over flavour and carbonation.",
                image: "https://customer-assets-eiarnc6j.emergentagent.net/job_beverage-tech-1/artifacts/0o6f0wl1_21032026-IMG_5962.webp" },
              { icon: Package, title: "Signature Serves", body: "Original drinks designed around fine, lively carbonation.",
                image: "https://customer-assets-eiarnc6j.emergentagent.net/job_beverage-tech-1/artifacts/jmb44c94_16052026-IMG_0243.webp" },
              { icon: Repeat, title: "Sparkling Water", body: "Clean, crisp bubbles — ready in minutes, anywhere.",
                image: "https://customer-assets-eiarnc6j.emergentagent.net/job_beverage-tech-1/artifacts/9sks29nk_Captura%20de%20ecr%C3%A3%202026-09-04%20122152.png" },
            ].map(({ icon: Icon, title, body, image }) => (
              <div
                key={title}
                className="relative rounded-2xl overflow-hidden bg-[#111] border border-white/10 aspect-[4/5] group hover:border-[#00B5D8]/40 transition-colors"
              >
                {image ? (
                  <>
                    <img
                      src={image}
                      alt={title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                    <div className="relative h-full flex flex-col justify-end p-5">
                      <p className="preshh-card-title text-white">{title}</p>
                      <p className="preshh-card-body mt-2 text-white/70">{body}</p>
                    </div>
                  </>
                ) : (
                  <div className="relative h-full flex flex-col justify-between p-5">
                    <Icon size={26} strokeWidth={1.4} className="text-[#00B5D8]" />
                    <div>
                      <p className="preshh-card-title">{title}</p>
                      <p className="preshh-card-body mt-2">{body}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KEVIN KOS */}
      <section className="section-pad max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-[#111]">
            <img src={BRAND.kevin}
              alt="Kevin Kos" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-display text-5xl">KEVIN KOS</p>
              <p className="font-display text-2xl text-[#00B5D8]">WORLD SHELF</p>
            </div>
          </div>
          <div>
            <p className="preshh-eyebrow mb-4">Trusted by professionals</p>
            <h2 className="preshh-section-title max-w-lg">An international reference in modern mixology.</h2>
            <p className="mt-6 preshh-section-lead">
              Kevin Kos inspires bartenders through his YouTube and Instagram content, reaching millions of views. For his bar and content creation, he chose Preshh as his carbonation solution — showcasing the technology to a global audience and becoming one of the brand's leading references within the international bartending community.
            </p>
          </div>
        </div>
      </section>

      {/* AUTHENTIC PROFESSIONAL TESTIMONIALS */}
      <section className="section-pad bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <p className="preshh-eyebrow mb-3">{t("sections.voices_kicker")}</p>
              <h2 className="preshh-section-title max-w-2xl">{t("sections.voices_title")}</h2>
            </div>
          </div>
          <Carousel opts={{ align: "start", loop: true }} setApi={setCarouselApi} className="w-full" data-testid="voices-carousel">
            <CarouselContent className="-ml-4" aria-live="off">
              {testimonials.map((tm) => {
                const lang = (i18n.language || "en").split("-")[0];
                const quote = tm[`quote_${lang}`] || tm.quote;
                return (
                <CarouselItem key={tm.id} className="pl-4 md:basis-1/2 lg:basis-1/2">
                  <div className="h-full relative bg-[#111] border border-white/10 rounded-3xl overflow-hidden group hover:border-[#00B5D8]/30 transition-colors flex flex-col">
                    {tm.image ? (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={tm.image} alt={`${tm.name} — ${tm.role || "Preshh"}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent pointer-events-none" />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] flex items-center justify-center border-b border-white/10">
                        <p className="text-white/20 font-display text-6xl" aria-hidden="true">"</p>
                      </div>
                    )}
                    <div className="p-6 md:p-8 flex flex-col flex-1">
                      {quote ? (
                        <blockquote className="text-white/85 leading-relaxed text-[15px] italic whitespace-pre-line">"{quote}"</blockquote>
                      ) : (
                        <p className="text-white/40 italic text-sm">{t("sections.testimonial_pending")}</p>
                      )}
                      <div className="mt-5 pt-4 border-t border-white/10">
                        <p className="font-bold text-sm">{tm.name}</p>
                        <p className="text-xs text-white/60 mt-0.5">{tm.role}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
                );
              })}
            </CarouselContent>
            <div className="flex justify-end gap-3 mt-8">
              <CarouselPrevious data-testid="voices-prev" className="static translate-y-0 h-11 w-11 bg-[#111] border-white/15 hover:bg-[#00B5D8] hover:text-black hover:border-[#00B5D8] text-white" />
              <CarouselNext data-testid="voices-next" className="static translate-y-0 h-11 w-11 bg-[#111] border-white/15 hover:bg-[#00B5D8] hover:text-black hover:border-[#00B5D8] text-white" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* COMPARE */}
      <section className="section-pad max-w-7xl mx-auto">
        <p className="preshh-eyebrow mb-3">Choose your Preshh</p>
        <h2 className="preshh-section-title mb-12 max-w-2xl">Two systems. One philosophy.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#111] border border-white/10 rounded-3xl p-8 hover:border-[#00B5D8]/40 transition-colors">
            <span className="preshh-badge-go">Preshh GO · Consumer</span>
            <h3 className="preshh-card-title text-3xl mt-4">Portable. Personal.</h3>
            <ul className="mt-6 space-y-2 preshh-card-body text-white/75">
              <li>· Portable, lower-pressure system</li>
              <li>· Works with compatible standard PET bottles</li>
              <li>· Home users & small businesses</li>
              <li>· Cocktails, sodas, spritzes, creative drinks</li>
              <li>· One-time purchase</li>
            </ul>
            <Link to="/preshhgo" className="mt-8 inline-flex preshh-btn-primary">Buy Preshh GO</Link>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-3xl p-8 hover:border-[#C9A96E]/40 transition-colors">
            <span className="preshh-badge-pro">Preshh PRO · Professional</span>
            <h3 className="preshh-card-title text-3xl mt-4">Higher Pressure. Full Service.</h3>
            <ul className="mt-6 space-y-2 preshh-card-body text-white/75">
              <li>· Bars, restaurants, hotels, events</li>
              <li>· Foams, infusions, macerations</li>
              <li>· Complex sodas & cocktail batches</li>
              <li>· Monthly rental (10 bottles included)</li>
              <li>· Continuous support · Cancel anytime</li>
            </ul>
            <Link to="/professional" className="mt-8 inline-flex preshh-btn-pro">Talk to a Consultant</Link>
          </div>        </div>
      </section>

      {/* ACCESSORIES */}
      <section className="section-pad bg-[#0a0a0a] border-y border-white/5" aria-labelledby="setup-title">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-4">
            <h2 id="setup-title" className="preshh-section-title">Complete your setup.</h2>
            <Link to="/shop" className="preshh-btn-ghost text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]">Shop all accessories →</Link>
          </div>
          <p className="mb-10 text-white/60 text-sm md:text-base max-w-xl">Everything you need to get more from your Preshh — bottles, caps, regulator and CO₂ essentials.</p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 list-none p-0 m-0">
            {accessories.map((p) => (
              <li key={p.id}>
                <Link to={`/product/${p.slug}`} data-testid={`setup-card-${p.slug}`}
                  aria-label={`${p.name}, ${format(p.price)} — view product`}
                  className="group flex flex-col h-full rounded-2xl bg-[#111] border border-white/10 overflow-hidden hover:border-[#00B5D8]/40 hover:-translate-y-0.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]">
                  <div className="aspect-square bg-white overflow-hidden">
                    <img src={p.images[0]} alt={`${p.name} — Preshh accessory`} loading="lazy"
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="preshh-card-title text-[15px] leading-snug">{p.name}</p>
                    <p className="text-sm text-[#00B5D8] mt-1">{format(p.price)} <span className="text-white/50 text-xs">{t("vat_suffix")}</span></p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-white/70 group-hover:text-[#00B5D8] transition-colors">
                      View product <ArrowRight size={13} aria-hidden="true" className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-pad max-w-7xl mx-auto text-center">
        <h2 className="font-display text-5xl md:text-7xl leading-none tracking-tight">Ready to<br/><span className="text-[#00B5D8]">carbonate?</span></h2>
        <p className="mt-6 preshh-section-lead max-w-xl mx-auto">Bring professional carbonation home. One kit, endless possibilities.</p>
        <Link to="/preshhgo" className="mt-8 inline-flex preshh-btn-primary">Buy Preshh GO</Link>
      </section>
    </div>
  );
}
